import React, { useState } from 'react'
import {
  NumberInput,
  Table,
  TableBody,
  TableRow,
  TableBodyCell,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { round } from 'vend-number'
import { formatCurrencyValue } from '@vend/utilities'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { DECIMAL_PLACES } from '../useFormatClosureNumber'

interface RegisterClosureCashCountingTotalProps {
  paymentName: string
  /** Rounded total for read-only display (denomination mode). */
  total: string
  /** When set, the amount cell is an editable total instead of formatted text. */
  totalEditable?: boolean
  editableValue?: string
  onEditableValueChange?: (val: string) => void
  decimalSeparator?: string
}

/**
 * Counted cash total row for register closure (read-only from denominations, or direct total input).
 */
export const RegisterClosureCashCountingTotal: React.FC<RegisterClosureCashCountingTotalProps> = ({
  paymentName,
  total,
  totalEditable = false,
  editableValue = '',
  onEditableValueChange,
  decimalSeparator = '.',
}) => {
  const t = useT()
  const [error, setError] = useState(false)
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()

  if (!retailer) {
    return null
  }

  return (
    <Table className="wr-closure-cash-counting-total-card vd-mt10">
      <TableBody>
        <TableRow className="wr-closure-cash-counting-grand-total">
          <TableBodyCell colSpan={2} className="vd-align-left">
            <strong>
              <T
                _str="{paymentName} TOTAL"
                paymentName={paymentName.toUpperCase()}
                _context="Total row label showing payment type total in register closure cash counting"
                _key="webregister.register-closure.cash-counting.total.label"
              />
            </strong>
          </TableBodyCell>
          <TableBodyCell className="vd-align-right wr-closure-cash-counting-total-amount">
            {totalEditable && onEditableValueChange ? (
              <NumberInput
                value={editableValue}
                onChange={val => {
                  onEditableValueChange(val.toString())
                }}
                onBlur={() => {
                  if (editableValue) {
                    onEditableValueChange(round(editableValue, DECIMAL_PLACES))
                    if (error) {
                      setError(false)
                    }
                  }
                }}
                onError={() => {
                  setError(true)
                }}
                error={
                  error
                    ? t('Invalid amount', {
                        _context:
                          'Generic error message for invalid register closure fields',
                        _key:
                          'webregister.register-closure.cash-counting.total.invalid-amount',
                      })
                    : undefined
                }
                placeholder="0.00"
                decimalSeparator={decimalSeparator}
                data-testid="cash-total-only-input"
                selectTextOnClick
              />
            ) : (
              <strong data-testid="cash-counting-total">
                {formatCurrencyValue(
                  total,
                  retailer.culture,
                  currentOutlet.currency,
                  currentOutlet.currency_symbol
                )}
              </strong>
            )}
          </TableBodyCell>
        </TableRow>
      </TableBody>
    </Table>
  )
}
